/**
 * English dictionary.
 *
 * `Messages`가 `ko.ts`의 키 집합을 그대로 요구하므로, 키가 빠지거나 남으면 컴파일이 막힌다.
 * 여기서 키를 새로 만들지 마라. 원본은 언제나 `ko.ts`다.
 *
 * ## 번역이 아니라 다시 쓰기다
 *
 * 한국어 문장을 그대로 옮기면 영어가 설명문이 된다. 같은 **역할**을 하는 짧은 영어 문장을
 * 새로 쓴다. 축약형(you're, it's)은 피한다. 영어가 모국어가 아닌 플레이어가 훨씬 많고,
 * 작은 글씨에서 아포스트로피는 먼저 사라지는 획이다.
 *
 * 용어는 GLOSSARY.md에 고정되어 있다. "이상한 곳"은 언제나 oddity이고,
 * "원래 방"은 언제나 original room이다. 화면마다 다른 말을 쓰면 같은 규칙이 둘로 보인다.
 *
 * 감식 어휘도 한 벌로 고정한다(`ko.ts`의 같은 절): 현장 = SCENE, 원본 = ORIGINAL,
 * 대조 = compare. 영어에서 crime scene 쪽 어휘(case, evidence, suspect)를 쓰지 않는 이유는
 * 한국어와 같다 - 파티게임이라 무거워지면 실패다.
 */

import type { Messages } from './ko'

export const en: Messages = {
  /* --- common -------------------------------------------------------------- */

  'common.appName': 'Weird Room',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.none': 'None',
  'common.separator': ', ',

  /* --- language ------------------------------------------------------------ */

  'lang.label': 'Language',
  'lang.ko': '한국어',
  'lang.en': 'English',

  /* --- verbs (kinds of oddity) --------------------------------------------- */

  'verb.add': 'New',
  'verb.remove': 'Gone',
  'verb.move': 'Moved',
  'verb.rotate': 'Turned',
  'verb.recolor': 'Recolored',

  /* --- rooms --------------------------------------------------------------- */

  'room.room': 'Room',
  'room.living': 'Living room',
  'room.kitchen': 'Kitchen',
  'room.bedroom': 'Bedroom',
  'room.bedroom-main': 'Main bedroom',
  'room.bedroom-guest': 'Spare room',
  'room.hall': 'Hallway',
  'room.bath': 'Bathroom',
  'room.study': 'Study',
  'room.hall-west': 'West hallway',
  'room.hall-mid': 'Middle hallway',
  'room.hall-east': 'East hallway',

  /* --- title screen -------------------------------------------------------- */
  /* 이름은 `common.appName`이 그대로 선다. 여기 키는 버튼 하나뿐이다(`ko.ts`의 같은 절). */

  'title.start': 'Start',


  /* --- menu (roadmap M13) --------------------------------------------------- */

  'menu.eyebrow': 'What now',
  'menu.title': 'Weird Room',
  'menu.go': 'Enter',
  'menu.back': 'Menu',
  'menu.cardAria': '{title}. {status}',
  'menu.solo.eyebrow': 'Solo',
  'menu.solo.title': 'Solo practice',
  'menu.solo.desc': 'Ten steps in order, one rule at a time',
  'menu.solo.chipNext': 'Step {step} next',
  'menu.party.title': "Everyone's room",
  'menu.party.desc': 'Search the same room together',

  /* --- stage select -------------------------------------------------------- */

  'select.eyebrow': 'Solo practice',
  'select.title': 'Weird Room',
  'select.progress': '{cleared} / {total} cleared',
  'select.footInProgress': 'Clear one to open the next',
  'select.footGraduated': 'All clear. Time to bring friends in',
  'select.locked': 'Clear stage {step} to open this',
  'select.startHere': 'Start here',
  'select.noRecord': 'No record yet',
  'select.best': 'Best {score}, {found}/{total}, {time}',
  /* 점수만 아는 기록. 이유는 `ko.ts`의 같은 자리에. */
  'select.bestScoreOnly': 'Best {score}',
  'select.stateCleared': 'Cleared',
  'select.stateCurrent': 'Up next',
  'select.stateLocked': 'Locked',
  'select.cardAria': 'Stage {step}, {name}. {state}. {status}',
  'select.chipOddities': '{count} {count|oddity|oddities}',
  'select.chipObjects': 'about {count} things',
  'select.chipTimeAria': '{time} on the clock',

  /* --- stage names and hints ----------------------------------------------- */

  'level.1.name': 'First Room',
  'level.1.hint': 'One thing goes missing. Shoot the empty spot.',
  'level.2.name': 'One More Thing',
  'level.2.hint': 'Things can appear too. Watch for gaps and for strangers.',
  'level.3.name': 'Something Slid',
  'level.3.hint': 'Things slide around. Counting is not enough. Watch the gaps by the walls.',
  'level.4.name': 'Two Rooms',
  'level.4.hint': 'Finish one room before you cross over. Go back and forth and you remember neither.',
  'level.5.name': 'Turned Around',
  'level.5.hint': 'Things turn. Which way was the chair facing?',
  'level.6.name': 'Small House',
  'level.6.hint': 'Furniture sits in clusters. Remember the cluster, not the piece.',
  'level.7.name': 'The Wrong Color',
  'level.7.hint': 'Colors shift. When you see twins, compare them.',
  'level.8.name': 'Back to the Original',
  'level.8.hint': 'You can visit the original room. Step out and you wait 5 seconds.',
  'level.9.name': 'Apartment',
  'level.9.hint': 'Nine spaces. Watch the clock and your shots, then pick your order.',
  'level.10.name': 'Weird Room',
  'level.10.hint': 'Same rules as a real party. If you got here, you are ready.',

  /* --- stage intro --------------------------------------------------------- */

  'intro.step': 'Stage {order}',
  'intro.ruleOddities': 'Oddities',
  'intro.ruleTime': 'Time',
  'intro.ruleAmmo': 'Shots',
  'intro.oddityValue': '{count}',
  'intro.ammoValue': '{count}',
  'intro.verbsLabel': 'What can go odd this stage',
  'intro.newBadge': 'NEW',
  'intro.start': 'Go in',
  'intro.back': 'Stages',

  /* --- phases -------------------------------------------------------------- */

  'phase.watch.name': 'Memorize',
  'phase.watch.hint': 'Memorize the room',
  'phase.skip.votes': '{text} {votes}/{needed}',
  'phase.watch.skip': 'Skip to search',
  /* 도장 두 장. 있고 없음이 곧 "그 페이즈에 도장이 있는가"다(`ko.ts`의 같은 절). */
  'phase.watch.stamp': 'MEMORIZE!',
  'phase.hunt.name': 'Search',
  'phase.hunt.hint': 'Shoot what is off',
  'phase.hunt.skip': 'End search',
  'phase.watch.stamp.stampDetective': 'TAKE IT IN!',
  'phase.watch.stamp.stampDeadpan': 'Look at it.',
  'phase.watch.stamp.stampLoud': 'LOOK HARDER!!',
  'phase.watch.stamp.stampProud': 'This room again.',
  'phase.watch.stamp.stampRookie': "MEMORIZING IT ALL!",
  'phase.watch.stamp.stampClerk': "Verify the original.",
  'phase.watch.stamp.stampGuard': "Eyes on patrol.",
  'phase.watch.stamp.stampCourier': "Check before you sign!",
  'phase.watch.stamp.stampTeacher': "Now, look closely.",
  'phase.watch.stamp.stampBoss': "Commit it to memory.",
  'phase.watch.stamp.stampReporter': "START TAKING NOTES!",
  'phase.watch.stamp.stampCleaner': "Look before I tidy.",
  'phase.watch.stamp.stampLibrarian': "Memorize it quietly.",
  'phase.watch.stamp.stampScared': "Don't look away!",
  'phase.watch.stamp.stampPolite': "Please take a look.",
  'phase.watch.stamp.stampSleepy': "Yawn... take a look.",
  'phase.watch.stamp.stampHasty': "MEMORIZE, QUICK!",
  'phase.watch.stamp.stampKind': "Take your time looking.",
  'phase.watch.stamp.stampBored': "Look at it, whatever.",
  'phase.watch.stamp.stampFlustered': "Oh my, memorize it!",
  'phase.watch.stamp.stampShy': "Um... have a look.",
  'phase.watch.stamp.stampGrumpy': "LOOK PROPERLY!",
  'phase.watch.stamp.stampPlayByPlay': "EYES ON THE ROOM!",
  'phase.watch.stamp.stampBreakingNews': "LIVE FROM THE SCENE.",
  'phase.watch.stamp.stampAd': "DON'T MISS A THING!",
  'phase.watch.stamp.stampAnnouncement': "Please look around now.",
  'phase.watch.stamp.stampPoem': "Gaze a long while.",
  'phase.watch.stamp.stampFolkTale': "Look well, now.",
  'phase.watch.stamp.stampManual': "Step 1. Observe.",
  'phase.watch.stamp.stampDiary': "Remember this room.",
  'phase.watch.stamp.stampMachine': "SCAN START.",
  'phase.hunt.stamp': 'SOMEONE WAS HERE!',
  'phase.hunt.stamp.stampDetective': 'A SCENE!',
  'phase.hunt.stamp.stampDeadpan': 'Something moved.',
  'phase.hunt.stamp.stampLoud': 'SOMEONE CAME BY!!',
  'phase.hunt.stamp.stampProud': 'I see all of it.',
  'phase.hunt.stamp.stampRookie': "WAIT, IT CHANGED!",
  'phase.hunt.stamp.stampClerk': "Filing this one.",
  'phase.hunt.stamp.stampGuard': "Not all clear!",
  'phase.hunt.stamp.stampCourier': "Someone left one here!",
  'phase.hunt.stamp.stampTeacher': "What's different?",
  'phase.hunt.stamp.stampBoss': "Who touched this?",
  'phase.hunt.stamp.stampReporter': "THIS IS THE STORY!",
  'phase.hunt.stamp.stampCleaner': "Someone made a mess.",
  'phase.hunt.stamp.stampLibrarian': "That's out of place.",
  'phase.hunt.stamp.stampScared': "It changed! Oh no!",
  'phase.hunt.stamp.stampPolite': "Something has changed.",
  'phase.hunt.stamp.stampSleepy': "Mm. Something moved.",
  'phase.hunt.stamp.stampHasty': "IT CHANGED. GO!",
  'phase.hunt.stamp.stampKind': "Something's different.",
  'phase.hunt.stamp.stampBored': "Changed, apparently.",
  'phase.hunt.stamp.stampFlustered': "Goodness, it changed!",
  'phase.hunt.stamp.stampShy': "Someone came by, maybe.",
  'phase.hunt.stamp.stampGrumpy': "SOMEONE MESSED WITH IT!",
  'phase.hunt.stamp.stampPlayByPlay': "AND IT'S CHANGED!",
  'phase.hunt.stamp.stampBreakingNews': "BREAKING NEWS.",
  'phase.hunt.stamp.stampAd': "NEW AND IMPROVED!",
  'phase.hunt.stamp.stampAnnouncement': "A change has been made.",
  'phase.hunt.stamp.stampPoem': "Something has moved away.",
  'phase.hunt.stamp.stampFolkTale': "By night, it changed.",
  'phase.hunt.stamp.stampManual': "Step 2. Spot the change.",
  'phase.hunt.stamp.stampDiary': "Not as I left it.",
  'phase.hunt.stamp.stampMachine': "MISMATCH FOUND.",
  'phase.reveal.name': 'Reveal',
  'phase.reveal.hint': 'What you missed',
  'phase.reveal.skip': 'Skip',
  'phase.result.name': 'Result',
  'phase.result.hint': '',

  /* 여럿이의 세 페이즈. 이름은 용어집 1.5절(Decorate / Meddle). */
  'phase.lobby.name': 'Gather',
  'phase.lobby.hint': 'Starts when everyone is in',
  'phase.lobby.skip': 'Start now',
  'phase.decorate.name': 'Decorate',
  'phase.decorate.hint': 'Place your props',
  'phase.decorate.skip': 'Done placing',
  'phase.change.name': 'Meddle',
  'phase.change.hint': 'Meddle with your own zone',
  'phase.change.skip': 'Skip to memorizing',
  'phase.change.stamp': 'MEDDLE!',
  'phase.change.stamp.stampDetective': 'LEAVE A TRACE!',
  'phase.change.stamp.stampDeadpan': 'Touch something.',
  'phase.change.stamp.stampLoud': 'TURN IT ALL OVER!!',
  'phase.change.stamp.stampProud': 'Go on, find it.',
  'phase.change.stamp.stampRookie': "I'LL CHANGE ONE!",
  'phase.change.stamp.stampClerk': "Amend one line.",
  'phase.change.stamp.stampGuard': "Shift it out of place.",
  'phase.change.stamp.stampCourier': "Leave one behind!",
  'phase.change.stamp.stampTeacher': "Change just one.",
  'phase.change.stamp.stampBoss': "You. Change it.",
  'phase.change.stamp.stampReporter': "MAKE ME A HEADLINE!",
  'phase.change.stamp.stampCleaner': "Go make a mess.",
  'phase.change.stamp.stampLibrarian': "Move just one.",
  'phase.change.stamp.stampScared': "Do I have to change it?",
  'phase.change.stamp.stampPolite': "Would you change one?",
  'phase.change.stamp.stampSleepy': "Change it, then bed.",
  'phase.change.stamp.stampHasty': "CHANGE IT, NOW!",
  'phase.change.stamp.stampKind': "Your turn. Change one.",
  'phase.change.stamp.stampBored': "Move anything, sure.",
  'phase.change.stamp.stampFlustered': "Go on, change one!",
  'phase.change.stamp.stampShy': "I want a turn too...",
  'phase.change.stamp.stampGrumpy': "NOW YOU GO CHANGE IT!",
  'phase.change.stamp.stampPlayByPlay': "AND NOW, YOUR MOVE!",
  'phase.change.stamp.stampBreakingNews': "YOU'RE LIVE. CHANGE IT.",
  'phase.change.stamp.stampAd': "CHANGE IT TODAY!",
  'phase.change.stamp.stampAnnouncement': "Please make a change.",
  'phase.change.stamp.stampPoem': "Scatter it like wind.",
  'phase.change.stamp.stampFolkTale': "Now you change it.",
  'phase.change.stamp.stampManual': "Step 3. Move something.",
  'phase.change.stamp.stampDiary': "I move one thing.",
  'phase.change.stamp.stampMachine': "EDIT PERMITTED.",

  /* --- in-game HUD --------------------------------------------------------- */

  'hud.found': 'Found',
  'hud.foundOf': '{found} / {total}',
  'hud.ammoUnit': 'left',
  'hud.ammoLow': 'Running low',
  'hud.ammoEmpty': 'Out of shots. Round is over',
  'hud.ammoGain.label': '+{amount}',
  'hud.ammoGain.announce': 'Used a magazine. {amount} shots loaded. {ammo} now',
  'hud.ammoBlocked': 'No shooting in the original room',

  /* --- magazines ----------------------------------------------------------- */

  'hud.mag.label': 'Mags',
  'hud.mag.aria': '{count} magazines. Tap to refill shots',
  'hud.mag.reload': 'Use a magazine',
  'hud.mag.roundSpent': 'No more magazines this round',
  /* 상태가 아니라 다음 행동. 왜 숫자가 필요한지는 `ko.ts`의 같은 자리에. */
  'hud.mag.needRoomCount': 'Fire {count} more {count|shot|shots} to fit a magazine',
  'hud.mag.needRoom': 'A magazine needs room. Fire more, then tap',
  'hud.originHint': 'Original room. No shooting',
  /* 이유는 `ko.ts`의 같은 자리에. 조준하는 방과 판정하는 방이 갈리는 0.4초다. */
  'hud.warpHint': 'The room is still changing. No shooting',
  'hud.originBadge': 'Original room',
  'hud.origin.ready.label': 'Original room',
  'hud.origin.ready.sub': 'Compare with the original',
  'hud.origin.staying.label': 'Out in {seconds}',
  'hud.origin.staying.sub': 'Leaves on its own',
  'hud.origin.cooldown.label': 'Back in {seconds}',
  'hud.origin.cooldown.brief': 'Opening up',
  'hud.origin.cooldown.sub': 'Not open yet',

  /* --- scene marks --------------------------------------------------------- */
  /* 자리는 방송 시절(REC · PLAYBACK · CAM 01) 그대로, 뜻만 감식으로 바뀌었다. */

  'hud.tag.scene': 'SCENE',
  'hud.tag.origin': 'ORIGINAL',
  'hud.tag.sceneNo': 'WEIRD ROOM SCENE 01',

  /* --- combo chip ---------------------------------------------------------- */

  'hud.combo.label': 'COMBO {count}',
  'hud.combo.labelCap': 'COMBO {count} MAX',

  'raise.chip.label': 'Double down',
  'raise.chip.on': 'Doubled',
  'raise.chip.aria': 'Double down. Finds score double from here, and a miss costs more shots',
  'raise.chip.onAria': 'Doubled down. Finds score double, and a miss costs more shots',
  'raise.chip.lockedAria': 'Double down. {reason}',
  'raise.lock.already': 'Already doubled down. Once per round',
  'raise.lock.ammo': 'Not enough shots to double down',
  'raise.notice.self': 'Doubled down. Finds score double from here',

  /* --- settings ------------------------------------------------------------ */

  'settings.title': 'Settings',
  'settings.open': 'Open settings',
  'settings.sound': 'Sound',
  'settings.bgm': 'Music',
  'settings.haptics': 'Haptics',
  'settings.language': 'Language',
  'settings.aria.on': '{name} on. Press to turn off',
  'settings.aria.off': '{name} off. Press to turn on',
  'settings.aria.choice': '{name}. Currently {value}. Press for {next}',
  'hud.sound.aria.on': 'Sound on. Press to mute',
  'hud.sound.aria.off': 'Sound off. Press to unmute',

  /* --- shot feedback ------------------------------------------------------- */

  'shot.object.label': 'Got it',
  'shot.object.announce': 'Found one oddity',
  'shot.removed.label': 'Empty spot',
  'shot.removed.announce': 'Found the spot where something was taken',
  'shot.miss.label': '-1',
  'shot.miss.announce': 'Missed. One shot gone',
  'shot.already-found.label': 'Already found',
  'shot.already-found.announce': 'You already found that one',
  'shot.dry.label': 'No shots',
  'shot.dry.announce': 'No shots left, cannot fire',
  'shot.pending.label': 'Checking',
  'shot.pending.announce': 'Shot fired. Checking',

  /* --- discovery captions (`ui/juice.ts`) ---------------------------------- */

  'juice.hit.0': 'Got it',
  'juice.hit.1': 'Combo',
  'juice.hit.2': 'On fire',
  'juice.hit.3': 'Unreal',
  'juice.comboBreak': 'Combo lost',
  'juice.finale.title': 'All found',
  'juice.finale.sub': '{count} oddities',
  'juice.perfect.title': 'PERFECT',
  'juice.perfect.sub': 'Not a single miss',
  'juice.perfect.bonus': 'Bonus {bonus}',
  'juice.nobodySaw.title': 'NOBODY SAW IT!',
  'juice.nobodySaw.sub': 'And that was the easy one',

  /* --- hunt notices --------------------------------------------------------- */

  'notice.remaining': '{count} to go',

  /* --- reveal lines -------------------------------------------------------- */

  'reveal.add.inRoom': 'A new {item} showed up in the {room}',
  'reveal.add.plain': 'A new {item} showed up',
  'reveal.remove.inRoom': 'The {item} vanished from the {room}',
  'reveal.remove.plain': 'The {item} vanished',
  'reveal.move.inRoom': 'The {item} moved across the {room}',
  'reveal.move.plain': 'The {item} moved',
  'reveal.rotate.inRoom': 'The {item} turned around in the {room}',
  'reveal.rotate.plain': 'The {item} turned around',
  'reveal.recolor.inRoom': 'The {item} changed color in the {room}',
  'reveal.recolor.plain': 'The {item} changed color',
  'reveal.unknownItem': 'something',
  'reveal.party.foundBy': '{name} found it',
  'reveal.party.mine': 'You found it',
  'reveal.party.nobody': 'Nobody saw it',

  /* --- item names ------------------------------------------------------------ */

  /*
   * Lowercase, singular, no article. The sentences supply those:
   * 'A new {item} showed up' / 'The {item} vanished'.
   */
  'item.mug': 'mug',
  'item.book': 'book',
  'item.trash-bin': 'trash bin',
  'item.potted-plant': 'potted plant',
  'item.chair': 'chair',
  'item.bench': 'bench',
  'item.table-dining': 'dining table',
  'item.shelf-tall': 'tall shelf',
  'item.sofa': 'sofa',
  'item.rug-round': 'round rug',
  'item.cup': 'cup',
  'item.plate': 'plate',
  'item.bottle': 'bottle',
  'item.computer-mouse': 'mouse',
  'item.keyboard': 'keyboard',
  'item.doormat': 'doormat',
  'item.pillow': 'cushion',
  'item.laptop': 'laptop',
  'item.microwave': 'microwave',
  'item.lamp-table': 'table lamp',
  'item.nightstand': 'nightstand',
  'item.monitor': 'monitor',
  'item.cardboard-box': 'delivery box',
  'item.stool-bar': 'bar stool',
  'item.speaker': 'speaker',
  'item.mirror': 'mirror',
  'item.side-table': 'side table',
  'item.cabinet-tv': 'TV stand',
  'item.television': 'television',
  'item.desk': 'desk',
  'item.kitchen-cabinet': 'kitchen cabinet',
  'item.kitchen-stove': 'stove',
  'item.toilet': 'toilet',
  'item.bathroom-sink': 'bathroom sink',
  'item.chair-desk': 'desk chair',
  'item.plant-large': 'large potted plant',
  'item.lamp-floor': 'floor lamp',
  'item.bookcase-open': 'open bookcase',
  'item.wardrobe': 'wardrobe',
  'item.coat-rack': 'coat rack',
  'item.fridge': 'fridge',
  'item.bed-double': 'double bed',
  'item.bathtub': 'bathtub',
  'item.rug-rectangle': 'rectangular rug',
  'item.chair-rounded': 'arched chair',
  'item.chair-cantilever': 'cantilever chair',
  'item.bench-backrest': 'high-back bench',
  'item.stool-bar-square': 'square bar stool',
  'item.armchair': 'armchair',
  'item.recliner': 'recliner',
  'item.lounge-chair-wide': 'wide lounge chair',
  'item.ottoman': 'ottoman',
  'item.sofa-chaise': 'chaise sofa',
  'item.sofa-corner': 'corner sofa',
  'item.sofa-modern': 'modern sofa',
  'item.bed-single': 'single bed',
  'item.bed-bunk': 'bunk bed',
  'item.nightstand-legs': 'legged nightstand',
  'item.pillow-long': 'long cushion',
  'item.coat-hooks': 'wall coat hooks',
  'item.table-round': 'hexagonal dining table',
  'item.table-glass': 'glass dining table',
  'item.table-cross': 'cross-leg dining table',
  'item.table-cloth': 'draped dining table',
  'item.desk-corner': 'corner desk',
  'item.table-coffee': 'coffee table',
  'item.table-coffee-square': 'square coffee table',
  'item.table-coffee-glass': 'glass coffee table',
  'item.table-coffee-glass-square': 'square glass coffee table',
  'item.bookcase-open-low': 'low open shelf',
  'item.bookcase-doors': 'closed bookcase',
  'item.cabinet-tv-doors': 'closed TV stand',
  'item.cardboard-box-open': 'open delivery box',
  'item.kitchen-cabinet-corner': 'corner kitchen cabinet',
  'item.kitchen-cabinet-upper': 'upper kitchen cabinet',
  'item.kitchen-cabinet-upper-low': 'low upper cabinet',
  'item.kitchen-cabinet-upper-corner': 'corner upper cabinet',
  'item.bathroom-cabinet': 'mirrored bathroom cabinet',
  'item.bathroom-vanity': 'bathroom vanity',
  'item.lamp-square-table': 'square table lamp',
  'item.kitchen-sink': 'kitchen sink',
  'item.fridge-small': 'small fridge',
  'item.fridge-large': 'double-door fridge',
  'item.fridge-built-in': 'built-in fridge',
  'item.kitchen-bar': 'bar counter',
  'item.kitchen-bar-end': 'rounded bar counter',
  'item.range-hood': 'large range hood',
  'item.range-hood-modern': 'modern range hood',
  'item.toaster': 'toaster',
  'item.blender': 'blender',
  'item.coffee-machine': 'coffee machine',
  'item.washer': 'washing machine',
  'item.dryer': 'dryer',
  'item.washer-dryer-tower': 'stacked washer and dryer',
  'item.shower-booth': 'shower booth',
  'item.shower-booth-round': 'curved shower booth',
  'item.bathroom-sink-square': 'square bathroom sink',
  'item.television-vintage': 'vintage TV',
  'item.television-antenna': 'TV antenna',
  'item.radio': 'radio',
  'item.speaker-small': 'bookshelf speaker',
  'item.ceiling-fan': 'ceiling fan',
  'item.lamp-ceiling-square': 'ceiling light',
  'item.lamp-wall': 'wall light',
  'item.bear-trophy': 'mounted bear head',
  'item.lamp-floor-square': 'column floor lamp',
  'item.rug-square': 'square rug',
  'item.rug-oval': 'oval rug',
  'item.cup-coffee': 'coffee cup',
  'item.glass': 'glass',
  'item.glass-wine': 'wine glass',
  'item.plate-deep': 'deep plate',
  'item.bowl': 'bowl',
  'item.bowl-cereal': 'cereal bowl',
  'item.pot': 'pot',
  'item.pot-lid': 'pot lid',
  'item.pan': 'pan',
  'item.frying-pan': 'frying pan',
  'item.cutting-board': 'cutting board',
  'item.knife-block': 'knife block',
  'item.utensil-fork': 'fork',
  'item.utensil-spoon': 'spoon',
  'item.whisk': 'whisk',
  'item.mortar': 'mortar',
  'item.rolling-pin': 'rolling pin',
  'item.egg-cup': 'egg cup',
  'item.cupcake': 'cupcake',
  'item.bottle-ketchup': 'ketchup bottle',
  'item.can': 'can',
  'item.can-small': 'flat can',
  'item.soda-bottle': 'soda bottle',
  'item.carton': 'milk carton',
  'item.barrel': 'barrel',
  'item.bag': 'paper bag',
  'item.shaker-salt': 'salt shaker',
  'item.pepper-mill': 'pepper mill',
  'item.honey': 'honey jar',
  'item.peanut-butter': 'peanut butter jar',
  'item.apple': 'apple',
  'item.banana': 'banana',
  'item.bread': 'sliced bread',
  'item.loaf': 'loaf of bread',
  'item.cake': 'cake',
  'item.cookie': 'cookie',
  'item.croissant': 'croissant',
  'item.donut': 'donut',
  'item.muffin': 'muffin',
  'item.sandwich': 'sandwich',
  'item.pizza-box': 'pizza box',
  'item.pumpkin': 'pumpkin',
  'item.watermelon': 'watermelon',

  /* --- result -------------------------------------------------------------- */

  'result.eyebrow': 'Round over',
  'result.eyebrowLevel': 'Stage {order}, {name}',
  'result.attempt': 'try {n}',
  'result.end.all-found': 'Found them all',
  'result.end.out-of-ammo': 'Out of shots',
  'result.end.all-out': 'Everyone out of shots',
  'result.end.stalled': 'Nobody left to find them',
  'result.end.timeout': 'Time up',
  'result.end.skipped': 'Stopped',
  'result.headline.perfect': 'Flawless',
  'result.headline.most': 'You saw almost all of it',
  'result.headline.half': 'Half of them',
  'result.headline.few': 'Your eyes are not open yet',
  'result.headline.none': 'That one needs another go',
  'result.headline.fallback': 'Round over',
  'result.visitor.headline': 'Nobody found {count} of them',
  'result.visitor.headlineNone': 'They found every one',
  'result.cleared': 'Cleared',
  'result.fail.one': 'One more',
  'result.fail.two': 'So close',
  'result.fail.few': 'You are getting it',
  'result.fail.many': 'Look again and you will see it',
  'result.fail.fallback': 'One more',
  'result.goalFail': 'Find {remaining} more to clear. You need {min} of {total}',
  'result.goalCleared': 'Found {found} of the {min} you needed',
  'result.scoreUnit': 'pts',
  'result.score.find': 'Finds',
  'result.score.combo': 'Combo',
  'result.score.speed': 'Speed',
  'result.score.perfect': 'Perfect',
  'result.score.raise': 'Double down',
  'result.statFound': 'Found',
  'result.statCombo': 'Best combo',
  'result.comboValue': '{count} in a row',
  'result.statAmmo': 'Shots left',
  'result.statTime': 'Time taken',
  'result.perfectBadge': 'PERFECT',
  'result.missedTitle': 'Missed',
  'result.missedTitleCount': 'Missed {count}',
  'result.missedEmpty': 'You missed nothing',
  /* 방 번호와 판 링크가 여기 있었다(2026-09-02 제거 - `ko.ts`의 같은 자리 참고). */
  'result.playAgain': 'One more round',
  'result.retryFree': 'Same room, new oddities',
  'result.retryLevel': 'Try again, new oddities',
  'result.nextLevel': 'Next stage',
  'result.exit': 'Stages',
  'result.shortcut': '{key} = {label}',

  /* --- decorating (placement screen) --------------------------------------- */

  'editor.catalogTitle': 'Props',
  'editor.selectTool': 'Pick',
  'editor.colorTitle': 'Color',
  'editor.colorAria': '{name} color',
  'editor.rotateLockedChip': 'No turning',
  'editor.controlsTitle': 'Controls',
  'editor.statusTitle': 'Now',
  'editor.statPlaced': 'Placed',
  'editor.statSelected': 'Picked',
  'editor.statFacing': 'Facing',
  'editor.statHeight': 'Height',
  'editor.propCount': 'Props',
  'editor.roomCode': 'Room code',
  'editor.modePlace': 'Place',
  'editor.modeLook': 'Look',
  'editor.toLookMode': 'Switch to look mode. Tap to shoot',
  'editor.toPlaceMode': 'Switch to place mode. Tap to set a spot',
  'editor.tapShoots': 'Tap to shoot',
  'editor.tapPlaces': 'Tap to set a spot',
  'editor.pickPrompt': 'Pick something to place',
  'editor.confirmPlace': 'Place',
  'editor.confirmMove': 'Move',
  'editor.removeSelected': 'Take this one away',
  'editor.rotateSelected': 'Turn the prop 90 degrees',
  'editor.heightFloating': 'floating {level} up',
  'editor.heightOnSurface': 'On the surface',
  'editor.facingLocked': 'Looks the same from every side',
  'editor.facingFree': 'Press R to turn',
  'editor.viewLeft': 'Turn the view left 90 degrees',
  'editor.viewRight': 'Turn the view right 90 degrees',
  'editor.zoomIn': 'Zoom in',
  'editor.zoomOut': 'Zoom out',
  'editor.zoom': 'Zoom',
  'editor.footprint': '{name}, {cells} {cells|cell|cells}',

  /* --- placement rejections ------------------------------------------------ */

  'editor.reject.no-visible-change': 'Nobody would notice that',
  'editor.reject.cell-occupied': 'Something is already there',
  'editor.reject.out-of-bounds': 'That is outside the room',
  'editor.reject.placement-limit': 'You placed everything you had',
  'editor.reject.verb-not-allowed': 'Not allowed this round',
  'editor.reject.unknown-catalog': 'No such prop',
  'editor.reject.unknown-instance': 'That one is already gone',
  'editor.reject.already-changed': 'One change per thing',

  /* --- control help -------------------------------------------------------- */

  'controls.key.tap': 'Tap',
  'controls.key.tapAgain': 'Tap again',
  'controls.key.drag': 'Drag',
  'controls.key.wheel': 'Wheel or pinch',
  'controls.mode': 'Swap look and place',
  'controls.tap': 'Shoot, or set a spot',
  'controls.confirm': 'Lock it into that spot',
  'controls.drag': 'Turn the view',
  'controls.zoom': 'Zoom',
  'controls.turnView': 'Turn the view 90 degrees',
  'controls.rotateProp': 'Turn the prop. Reloads in look mode',
  'controls.nextColor': 'Next color',
  'controls.removeProp': 'Take it away',
  'controls.freeHeight': 'Float it off the floor',
  'controls.heightAdjust': 'Nudge the height',
  'controls.quickPick': 'Jump to a prop',
  'controls.deselect': 'Drop the selection',

  /* --- grades (envelope) --------------------------------------------------- */
  /*
   * 한국어와 **같은 문자열이다.** 라틴 약어라 번역할 것이 없다(`ko.ts`의 같은 자리).
   */

  'grade.common': 'N',
  'grade.uncommon': 'R',
  'grade.rare': 'SR',
  'grade.legend': 'SSR',

  /* --- kits ---------------------------------------------------------------- */

  'kit.magnifier.name': 'Magnifier',
  'kit.magnifier.desc': '15 more seconds to memorize',
  'kit.leisure.name': 'Leisure',
  'kit.leisure.desc': '10 more seconds in the original room',
  'kit.slowClock.name': 'Slow clock',
  'kit.slowClock.desc': '30 more seconds to search',
  'kit.spare.name': 'Spare',
  'kit.spare.desc': '2 more shots to start with',

  'kit.slotLabel': 'Kit',
  'kit.pickHint': 'Pack one, it stays with you all stage',
  'kit.pickAria': 'Pack the {name}. {desc}',
  'kit.have': '{count} left',
  'kit.picked': 'Packed',
  'kit.pickedName': 'Packed the {name}',
  'kit.none': 'Go empty handed',
  'kit.empty': 'Nothing to pack this time',
  'kit.emptyHint': 'Clear a stage and one shows up',
  'kit.effect.watchSeconds': 'Memorize +{count}s',
  'kit.effect.huntSeconds': 'Search +{count}s',
  'kit.effect.originStaySeconds': 'Original room +{count}s',
  'kit.effect.startAmmo': 'Shots +{count}',

  /* --- 매일의 방 (로드맵 M9) ---------------------------------------------------- */

  'daily.eyebrow': 'Today only',
  'daily.title': 'Room of the day',
  'daily.desc': 'Everyone gets the same room today. No kit, and only the first run is kept',
  'daily.start': 'Enter',
  'daily.again': 'Once more',
  'daily.streak': '{count} {count|day|days} in a row',
  'daily.streakNone': 'No streak yet',
  'daily.todayNone': "Today's room is still waiting",
  'daily.todayDone': 'Today {score}, {found} of {total}',
  'daily.todayDoneScore': 'Today {score}',
  'daily.practice': "Today's run is done. The next one is not kept",
  'daily.cardAria': 'Room of the day. {status}',

  /* --- looks --------------------------------------------------------------- */

  'look.markerPin.name': 'Pin',
  'look.markerCircle.name': 'Circle',
  'look.markerCross.name': 'Cross',
  'look.markerTriangle.name': 'Triangle',
  'look.markerStar.name': 'Star',
  'look.markerSquare.name': "Square",
  'look.markerBlank.name': "Blank square",
  'look.markerNestedSquare.name': "Nested square",
  'look.markerRuled.name': "Ruled square",
  'look.markerLattice.name': "Window grid",
  'look.markerHatch.name': "Hatched square",
  'look.markerShade.name': "Half-shaded square",
  'look.markerDogEar.name': "Dog-ear",
  'look.markerPunched.name': "Punched hole",
  'look.markerCornerFill.name': "Corner",
  'look.markerDiamond.name': "Diamond",
  'look.markerNestedDiamond.name': "Nested diamond",
  'look.markerBullseye.name': "Bullseye",
  'look.markerRing.name': "Ring",
  'look.markerHalfMoon.name': "Half circle",
  'look.markerMissingPiece.name': "Missing piece",
  'look.markerWedge.name': "Wedge",
  'look.markerCone.name': "Party hat",
  'look.markerStarSix.name': "Six-point star",
  'look.markerStarEight.name': "Eight-point star",
  'look.markerStarSolid.name': "Heavy star",
  'look.markerStarTwelve.name': "Twelve-point star",
  'look.markerPinwheel.name': "Pinwheel pin",
  'look.markerStarRinged.name': "Ringed star",
  'look.markerStarTeardrop.name': "Teardrop star",
  'look.markerStarHollow.name': "Hollow star",
  'look.markerStarPlump.name': "Plump star",
  'look.markerStarBead.name': "Bead star",
  'look.markerWhirl.name': "Whirl pin",
  'look.markerBlade.name': "Wing pin",
  'look.markerFlowerSix.name': "Six-petal flower",
  'look.markerFlower.name': "Flower",
  'look.markerFlowerWhite.name': "White flower",
  'look.markerFlowerEight.name': "Eight-petal flower",
  'look.markerSnowflake.name': "Snowflake",
  'look.markerFrostFlake.name': "Frost flake",
  'look.markerArrowUp.name': "Up arrow",
  'look.markerArrowDown.name': "Down arrow",
  'look.markerArrowRewind.name': "Rewind arrow",
  'look.markerArrowSwap.name': "Swapping arrows",
  'look.markerArrowDouble.name': "Double-line arrow",
  'look.markerInfinity.name': "Infinity",
  'look.markerThreeBars.name': "Three bars",
  'look.markerThreeDots.name': "Three dots",
  'look.markerThickPlus.name': "Thick plus",
  'look.markerFullBlock.name': "Quadrant square",
  'look.markerHalfBlock.name': "Split square",
  'look.markerShadeBlock.name': "Shaded block",
  'look.markerPulse.name': 'Pulsing pin',
  'look.markerSpin.name': 'Spinning pin',
  'look.markerCog.name': "Cog pin",
  'look.markerWheel.name': "Wheel pin",
  'look.markerCompass.name': "Compass pin",
  'look.markerRipple.name': "Ripple pin",
  'look.markerBeacon.name': "Beacon pin",
  'look.markerFloat.name': "Floating pin",
  'look.tagYellow.name': 'Yellow name tag',
  'look.tagMint.name': 'Mint name tag',
  'look.tagCoral.name': 'Coral name tag',
  'look.tagSky.name': 'Sky name tag',
  'look.tagLilac.name': 'Lilac name tag',
  'look.tagRose.name': "Rose name tag",
  'look.tagStrawberry.name': "Strawberry name tag",
  'look.tagCherry.name': "Cherry name tag",
  'look.tagPeach.name': "Peach name tag",
  'look.tagApricot.name': "Apricot name tag",
  'look.tagCopper.name': "Copper name tag",
  'look.tagIvory.name': "Ivory name tag",
  'look.tagChartreuse.name': "Chartreuse name tag",
  'look.tagMugwort.name': "Mugwort name tag",
  'look.tagSprout.name': "Sprout name tag",
  'look.tagMoss.name': "Moss name tag",
  'look.tagGrass.name': "Grass name tag",
  'look.tagPea.name': "Pea name tag",
  'look.tagGreen.name': "Green name tag",
  'look.tagEmerald.name': "Emerald name tag",
  'look.tagCeladon.name': "Celadon name tag",
  'look.tagJade.name': "Jade name tag",
  'look.tagTeal.name': "Teal name tag",
  'look.tagAqua.name': "Aqua name tag",
  'look.tagOcean.name': "Ocean name tag",
  'look.tagAsh.name': "Ash name tag",
  'look.tagSilver.name': "Silver name tag",
  'look.tagIndigo.name': "Indigo name tag",
  'look.tagCornflower.name': "Cornflower name tag",
  'look.tagNavy.name': "Ultramarine name tag",
  'look.tagHydrangea.name': "Hydrangea name tag",
  'look.tagIris.name': "Iris name tag",
  'look.tagWisteria.name': "Wisteria name tag",
  'look.tagViolet.name': "Violet name tag",
  'look.tagGrape.name': "Grape name tag",
  'look.tagAmethyst.name': "Amethyst name tag",
  'look.tagPlum.name': "Plum name tag",
  'look.tagPeony.name': "Peony name tag",
  'look.tagRouge.name': "Rouge name tag",
  'look.tagFuchsia.name': "Fuchsia name tag",
  'look.tagBlossom.name': "Blossom name tag",
  'look.stampBasic.name': 'Plain stamps',
  'look.stampDetective.name': 'Detective stamps',
  'look.stampDeadpan.name': 'Deadpan stamps',
  'look.stampLoud.name': 'Loud stamps',
  'look.stampRookie.name': "Rookie stamps",
  'look.stampClerk.name': "Clerk stamps",
  'look.stampGuard.name': "Guard stamps",
  'look.stampCourier.name': "Courier stamps",
  'look.stampTeacher.name': "Teacher stamps",
  'look.stampBoss.name': "Boss stamps",
  'look.stampReporter.name': "Reporter stamps",
  'look.stampCleaner.name': "Cleaner stamps",
  'look.stampLibrarian.name': "Librarian stamps",
  'look.stampScared.name': "Scared stamps",
  'look.stampPolite.name': "Polite stamps",
  'look.stampSleepy.name': "Sleepy stamps",
  'look.stampHasty.name': "Hasty stamps",
  'look.stampKind.name': "Kind stamps",
  'look.stampBored.name': "Unbothered stamps",
  'look.stampFlustered.name': "Flustered stamps",
  'look.stampShy.name': "Shy stamps",
  'look.stampGrumpy.name': "Angry stamps",
  'look.stampPlayByPlay.name': "Commentary stamps",
  'look.stampBreakingNews.name': "Breaking news stamps",
  'look.stampAd.name': "Ad stamps",
  'look.stampAnnouncement.name': "Announcement stamps",
  'look.stampPoem.name': "Poem stamps",
  'look.stampFolkTale.name': "Folk tale stamps",
  'look.stampManual.name': "Manual stamps",
  'look.stampDiary.name': "Diary stamps",
  'look.stampMachine.name': "Machine stamps",
  'look.frameNone.name': 'No frame',
  'look.frameTape.name': 'Masking tape frame',
  'look.frameClip.name': 'Clipboard frame',
  'look.frameLabel.name': "Label frame",
  'look.frameSealTape.name': "Sealing tape frame",
  'look.frameTearLine.name': "Tear line frame",
  'look.framePushpin.name': "Pushpin frame",
  'look.frameManuscript.name': "Manuscript frame",
  'look.frameFold.name': "Folded paper frame",
  'look.frameGlass.name': "Glass sheet frame",
  'look.frameIndexTab.name': "Index tab frame",
  'look.framePencil.name': "Pencil line frame",
  'look.frameFolder.name': "File folder frame",
  'look.frameRuler.name': "Ruler frame",
  'look.frameFilm.name': "Film strip frame",
  'look.frameSpine.name': "Book spine frame",
  'look.frameTwine.name': "Twine frame",
  'look.frameDoorJamb.name': "Door jamb frame",
  'look.frameWindowSill.name': "Window frame",
  'look.frameGrid.name': "Graph paper frame",
  'look.frameMatBoard.name': "Mat board frame",
  'look.envelopeKraft.name': 'Kraft envelope',
  'look.envelopeStriped.name': 'Striped envelope',
  'look.envelopeStamped.name': 'Stamped envelope',
  'look.envelopeGilt.name': 'Gilt envelope',
  'look.envelopeGrid.name': "Grid envelope",
  'look.envelopeLattice.name': "Lattice envelope",
  'look.envelopePinstripe.name': "Pinstripe envelope",
  'look.envelopeHatch.name': "Hatched envelope",
  'look.envelopeDiamond.name': "Diamond envelope",
  'look.envelopeCheck.name': "Checkered envelope",
  'look.envelopeRuled.name': "Ruled envelope",
  'look.envelopeDoubleRule.name': "Double rule envelope",
  'look.envelopeRipple.name': "Ripple envelope",
  'look.envelopeSpeckle.name': "Speckled envelope",
  'look.envelopeWoodGrain.name': "Woodgrain envelope",
  'look.envelopeCorrugated.name': "Corrugated envelope",
  'look.envelopeWideBand.name': "Wide band envelope",
  'look.envelopeHairline.name': "Hairline envelope",
  'look.envelopeAir.name': "Airmail envelope",
  'look.envelopeWindow.name': "Window envelope",
  'look.envelopePostmark.name': "Postmark envelope",
  'look.envelopePerf.name': "Perforated envelope",
  'look.envelopeCrease.name': "Creased envelope",
  'look.envelopeDoubleSeal.name': "Double-sealed envelope",
  'look.envelopeSilver.name': "Silver foil envelope",
  'look.envelopeCopper.name': "Copper envelope",
  'look.envelopeStain.name': "Stained envelope",
  'look.envelopeSunburst.name': "Sunburst envelope",
  'look.envelopeDiagonal.name': "Diagonal envelope",
  'look.envelopeDogEar.name': "Dog-eared envelope",
  'look.envelopeRegistered.name': "Registered envelope",
  'look.markerGlint.name': 'Glinting pin',
  'look.tagPink.name': 'Pink name tag',
  'look.stampProud.name': 'Smug stamps',
  'look.frameGallery.name': 'Gallery frame',
  'look.envelopeWax.name': 'Wax-sealed envelope',

  /* --- nicknames ----------------------------------------------------------- */

  'nickname.emptySeatSpotter': 'Empty Seat Spotter',
  'nickname.doorRegular': 'Regular at the Door',
  'nickname.chairCounter': 'Counts the Chairs',
  'nickname.cornerWatcher': 'Corner Watcher',
  'nickname.firstShotFirst': 'First Shot First',
  'nickname.lastSecondFinder': 'Last Second Finder',
  'nickname.turnedThingsNemesis': 'Nemesis of Turned Things',
  'nickname.colorHawk': 'Color Hawk',
  'nickname.noMissNoFuss': 'No Miss, No Fuss',
  'nickname.quietObserver': 'Quiet Observer',
  'nickname.visitor': 'The Visitor',
  'nickname.sameRouteEveryTime': "Same Route Every Time",
  'nickname.alwaysClockwise': "Always Clockwise",
  'nickname.oneLapOnArrival': "One Lap on Arrival",
  'nickname.windowsFirst': "Windows First",
  'nickname.looksUpToo': "Looks Up Too",
  'nickname.eyesOnTheFloor': "Eyes on the Floor",
  'nickname.behindTheFurniture': "Behind the Furniture",
  'nickname.tiltsTheHeadFirst': "Tilts the Head First",
  'nickname.rightUpClose': "Right Up Close",
  'nickname.takesAStepBack': "Takes a Step Back",
  'nickname.holdsABreath': "Holds Their Breath",
  'nickname.doubtsItFirst': "Doubts It First",
  'nickname.staresAtWhatStayed': "Stares at What Stayed",
  'nickname.sameSpotTwice': "Same Spot Twice",
  'nickname.countsTheShotsFirst': "Counts the Shots First",
  'nickname.alwaysOneLeft': "Always One Left",
  'nickname.onlyWhenSure': "Only When Sure",
  'nickname.allAtOnce': "All at Once",
  'nickname.shootAndMoveOn': "Shoot and Move On",
  'nickname.threeThenABreather': "Three Then a Breather",
  'nickname.keepsLookingAnyway': "Keeps Looking Anyway",
  'nickname.originalRoomAgain': "Original Room Again",
  'nickname.usesEverySecond': "Uses Every Second",
  'nickname.staysForTheResults': "Stays for the Results",
  'nickname.straightBackIn': "Straight Back In",
  'nickname.framesLevel': "Keeps Frames Level",
  'nickname.plantFriend': "Friend of the Plant",
  'nickname.lampOnly': "Eyes on the Lamp",
  'nickname.drawerGap': "One Drawer Off",
  'nickname.rugCorner': "Rug Corner Duty",
  'nickname.clockHands': "Clock Hands First",
  'nickname.cupHandle': "The Handle's Side",
  'nickname.cushionSquare': "Squares the Cushions",
  'nickname.bookSpines': "Spines First",
  'nickname.windowLatch': "Friend of the Latch",
  'nickname.doorknobEye': "Eyes on the Doorknob",
  'nickname.outletCount': "Counts the Outlets",
  'nickname.bulbDuty': "On Bulb Duty",
  'nickname.mirrorToo': "Checks the Mirror Too",
  'nickname.chairAngle': "Even the Chair Angle",
  'nickname.shelfLine': "Measures the Shelf Line",
  'nickname.curtainFolds': "Curtain Fold Duty",
  'nickname.switchWay': "Which Way the Switch Faces",
  'nickname.calendarPage': "One Calendar Page",
  'nickname.slipperPair': "Matches the Slippers",
  'nickname.trashBin': "Even the Bin",
  'nickname.remoteSpot': "Where the Remote Lives",
  'nickname.keysThere': "Keys Always There",
  'nickname.wallpaperPattern': "Down to the Wallpaper",
  'nickname.pencilOne': "Even One Pencil",
  'nickname.lastOneIn': "Last One In",
  'nickname.inNoHurry': "In No Hurry",
  'nickname.skipsTheBriefing': "Skips the Briefing",
  'nickname.cameOutEmptyHanded': "Came Out Empty-Handed",
  'nickname.justWarmingUp': "Just Warming Up",
  'nickname.nextRoundForReal': "Next Round for Real",
  'nickname.theGuessLanded': "The Guess Landed",
  'nickname.justAFeeling': "Just a Feeling",
  'nickname.luckyKindOfDay': "Lucky Kind of Day",
  'nickname.rightBehindYou': "Right Behind You",
  'nickname.trustsTheNeighbor': "Trusts the Next Seat",
  'nickname.gaspedAlong': "Gasped Along",
  'nickname.hereForTheRoom': "Here for the Room",
  'nickname.likesThePlace': "Likes the Place",
  'nickname.intoTheWallpaper': "Into the Wallpaper",
  'nickname.complimentsTheLighting': "Compliments the Lighting",
  'nickname.triesTheChair': "Tries the Chair",
  'nickname.wantsToTidyUp': "Wants to Tidy Up",
  'nickname.wouldLiveHere': "Would Live Here",
  'nickname.bigReactions': "Big Reactions",
  'nickname.clapsTheLoudest': "Claps the Loudest",
  'nickname.thinkingOutLoud': "Thinking Out Loud",
  'nickname.laughedAndMissed': "Laughed and Missed",
  'nickname.lostTrackOfTime': "Lost Track of Time",
  'nickname.eyesFirst': "Eyes Get There First",
  'nickname.quickHands': "Quick Hands",
  'nickname.fasterThanClock': "Faster Than the Clock",
  'nickname.allInOneGo': "All in One Go",
  'nickname.rightEveryTime': "Right Every Time",
  'nickname.noWastedMove': "No Wasted Move",
  'nickname.fastAndSure': "Fast and Sure",
  'nickname.firstGuessRight': "First Guess Is Right",
  'nickname.steadyHand': "Steady Hand",
  'nickname.neverRattled': "Never Rattled",
  'nickname.inNoRush': "In No Rush",
  'nickname.neverBlinks': "Never Blinks",
  'nickname.oneAfterAnother': "One After Another",
  'nickname.unbrokenRun': "Unbroken Run",
  'nickname.alwaysInForm': "Always in Form",
  'nickname.notAScratch': "Not a Scratch",
  'nickname.cleanRecord': "Clean Record",
  'nickname.cleanFinish': "Clean Finish",
  'nickname.comesFromBehind': "Comes From Behind",
  'nickname.turnsItAround': "Turns It Around",
  'nickname.oneForTheRecord': "One for the Record",
  'nickname.goodInstincts': "Good Instincts",
  'nickname.knowsWhereToLook': "Knows Where to Look",
  'nickname.seesItThrough': "Sees It Through",
  'nickname.patientEye': "The Patient Eye",
  'nickname.sawItAll': 'Not One Missed',

  /* --- party (roadmap M2) ---------------------------------------------------- */
  'match.foundBy': '{name} found it first',
  'match.spectating': 'Out of shots. You are spectating now',
  'match.doorOccupied': 'Someone is in the original room. You are {position} in line',
  'match.doorYourTurn': 'Your turn for the original room. Press within {seconds}s',
  'match.leave': 'Leave',
  'match.round': 'Round {round}',
  'wager.banner': '{name} doubled down!',
  'wager.banner.self': 'You doubled down!',
  'party.you': 'You',
  'party.botName': 'Neighbor {n}',
  'party.card.eyebrow': 'Party',
  'party.card.title': 'A round with friends',
  'party.card.desc': 'Make a room, share the code, and search it together. Neighbors fill the empty seats',
  'party.card.start': 'Go in',
  'party.card.chipSeats': 'Up to {max}',
  'party.card.aria': 'Party. {title}. {desc}',
  'party.hub.publicTag': 'Public',
  'party.hub.publicTitle': 'A round with anyone',
  'party.hub.publicDesc': 'Drop into a room that is open right now, or open one yourself',
  'party.browse.title': 'Open rooms',
  'party.browse.seats': '{players}/{max}',
  'party.browse.chipOddities': '{count} each',
  'party.browse.rowAria': "{host}'s room. {seats}",
  'party.browse.create': 'Open a room',
  'party.browse.refresh': 'Look again',
  'party.browse.loading': 'Looking for open rooms',
  'party.browse.empty': 'No rooms are open. Open one and it shows up here',
  'lobby.visibility.public': 'Public room',
  'lobby.visibility.private': 'Private room',
  'lobby.visibility.aria': 'Now {state}. Press to change',
  'party.hub.privateTag': 'Private',
  'party.hub.rankedTag': 'Soon',
  'party.hub.rankedTitle': 'Ranked',
  'party.hub.rankedDesc': 'Face a random opponent for rank',
  'party.hub.rankedLocked': 'Once accounts land',
  'party.level.easy': 'Easygoing neighbors',
  'party.level.normal': 'Regular neighbors',
  'party.level.hard': 'Sharp-eyed neighbors',
  'party.strip.aria': 'Players',
  'party.strip.found': '{count} found',
  'party.strip.rowAria': '{name}. {found} {found|oddity|oddities} found',
  'party.visitor': 'Visitor',
  'party.inOrigin': 'In the original',

  /* --- zones (roadmap M11) -------------------------------------------------- */

  'zone.assigned': 'The {name} is yours',
  'zone.none': 'No zone for you this round',
  'zone.notMine': 'Not your zone',
  'zone.mine': 'Your zone',
  'zone.mineAria': 'Your zone, the {name}',
  'zone.progress.title': 'Everyone is meddling',
  'zone.progress.aria': 'Zone progress',
  'zone.progress.rowAria': '{name}. {used} {used|oddity|oddities}, up to {budget}',
  'zone.progress.doneAria': '{name}. Done',
  'zone.progress.hint': 'Nothing has changed yet. Keep memorizing',
  'zone.reveal.owner': "{name}'s zone",
  'zone.reveal.ownerMine': 'Your zone',
  'zone.reveal.nobodyOwner': 'That scores for {name}',
  'zone.reveal.nobodyMine': 'That scores for you',
  'zone.result.label': 'Missed in your zone',
  'zone.result.value': '{count}',
  'zone.result.rowAria': '{count} {count|oddity|oddities} nobody found in your zone',
  'zone.lobby.note': 'One zone per player. Everyone meddles with their own',

  'spectate.badge': 'Spectating',
  'spectate.rowAria': '{name}. Spectating',
  'spectate.shotBlocked': 'Spectating. No shooting',
  'party.visitorShotBlocked': 'The visitor does not shoot',
  'spectate.watching': 'Watching {name}',
  'spectate.bar.aria': 'Pick a view',
  'spectate.target': "{name}'s view",
  'spectate.ticker.found': '{name} found the {item}',
  'door.aria': 'At the original room door',
  'door.holder': '{name} out in {seconds}',
  'door.holderMe': "You're inside. Out in {seconds}",
  'door.queueAria': '{count} in line',
  'door.queuedAria': '{name}. {position} in line',
  'door.yourTurn': 'Your turn {seconds}',
  'door.reservedFor': "{name}'s turn",

  'changer.barAria': 'Meddling tools',
  'changer.budgetAria': '{used} {used|oddity|oddities} made, up to {budget}',
  'changer.undo': 'Undo',
  'changer.undoAria': 'Undo {name}',
  'changer.done': 'Done',
  'changer.doneAria': 'Done. The remaining {left} will be filled in',
  'changer.hint.idle': 'Tap a thing to change it. Tap New to place something',
  'changer.hint.selected': '{name}. Pick how to change it',
  'changer.hint.moving': 'Tap where {name} should go',
  'changer.hint.adding': 'Tap where to put {name}',
  'changer.lockedAria': '{verb}. {reason}',
  'changer.lock.budget': 'You made everything you could',
  'changer.lock.no-target': 'Tap a thing first',
  'changer.catalogAria': 'Props to place',
  'changer.colorsAria': 'Colors',
  'changer.wager.label': 'They will miss',
  'changer.wager.none': 'Not doubling',
  'changer.wager.count': '{count}',
  'changer.wager.aria': 'They miss at least {count}. If so, your score doubles',
  'changer.wager.dec': 'One fewer',
  'changer.wager.inc': 'One more',
  'changer.wager.toggleAria': 'They will miss - {value}. Tap to choose',
  'changer.doneWagerAria': 'Done. Doubling down that they miss {count}',

  /*
   * Decorate bar (`ui/decorateBar.ts`). The hint line is the next thing to DO and shifts with what the
   * hand holds - pick one, place it, or (once spent) take one back. Telling a player who has none left
   * to "place a prop" is a dead end. There is no [Done placing] here; that vote lives in the HUD
   * (`phase.decorate.skip`).
   */
  'decorate.left': '{count} {count|prop|props} left to place',
  'decorate.hint.pick': 'Pick a prop',
  'decorate.hint.place': 'Tap an empty spot to place it',
  'decorate.hint.spent': 'All placed - tap your own prop to take it back',

  /*
   * First-time changer coach for party rounds (`ui/changerCoach.ts`). Each line is the next thing to DO,
   * not a paragraph to read - the title is the instruction, the hint is how. Verb words match the bar
   * (`verb.*`); calling it something else here means the player cannot find the button.
   */
  'coach.changer.place': 'Make an oddity in your zone',
  'coach.changer.place.hint': '[New] → pick a prop → drag to aim at a spot',
  'coach.changer.edit': 'You can redo what you placed',
  'coach.changer.edit.hint': 'Tap a prop to move, turn or recolor it. Redoing it still counts as one',
  'coach.changer.done': "That's all of them",
  'coach.changer.done.hint': '[Done], or let the clock run out.',
  'coach.changer.close': 'Got it',

  'verbCounts.item': '{count} {verb}',
  'verbCounts.notice': 'This time: {list}',

  'ranking.title': 'Ranking',
  'ranking.round': 'This round',
  'ranking.total': 'Total',
  'ranking.rank': '#{rank}',
  'ranking.rowAria': '#{rank} {name}, {round} this round, {total} total',

  /* --- pins ---------------------------------------------------------------- */

  'pins.chip.label': 'Pins',
  'pins.chip.aria': '{count} pins',
  'pins.result.label': 'Pins',
  'pins.result.value': '+{count} pins',
  'pins.gauge.ready': 'An envelope is ready to open',
  'pins.gauge.until': '{count} pins to an envelope',
  'pins.gauge.aria': '{pins} pins, {price} per envelope',

  /* --- envelope ------------------------------------------------------------ */

  'envelope.title': 'Envelope',
  'envelope.open': 'Open an envelope',
  'envelope.openAria': 'Open an envelope. {price} pins each',
  'envelope.price': '{price} pins',
  'envelope.oneMore': 'One more',
  'envelope.tenMore': 'Ten',
  'envelope.notEnough': 'Play {n} more {n|round|rounds} and this opens again',
  'envelope.seq': 'Envelope {seq}',
  'envelope.replay': 'Envelope {seq} again',
  'envelope.skipHint': 'Tap to jump to the card',
  'envelope.skip': 'Skip',
  'envelope.cardOf': '{index} / {total}',
  'envelope.oddsTitle': 'Odds',
  'envelope.odds': '{grade} {odds}',
  'envelope.pity.rare': 'SR within {count}',
  'envelope.pity.legend': 'SSR within {count}',
  'envelope.pity.rareNow': 'Next one is SR or better',
  'envelope.pity.legendNow': 'Next one is SSR',
  'envelope.forced.rare': 'SR guaranteed',
  'envelope.forced.legend': 'SSR guaranteed',
  'envelope.reward.magazines': '{count} {count|magazine|magazines}',
  'envelope.reward.pins': '{count} pins',
  'envelope.reward.kit': '{count} {name}',
  'envelope.reward.look': 'Look, {name}',
  'envelope.reward.nickname': 'Nickname, {name}',
  'envelope.duplicate': 'Already had it, so {count} pins instead',
  'envelope.overflow': 'Magazines are full, so {count} pins instead',
  'envelope.magsChip': 'Mags',
  'envelope.magsGain': '+{count}',

  /* --- locker -------------------------------------------------------------- */

  'locker.title': 'Locker',
  'locker.open': 'Open the locker',
  'locker.eyebrow': 'What you have',
  'locker.equipped': 'Equipped',
  'locker.equipAria': 'Equip {name}',
  'locker.unequipNickname': 'Remove nickname',
  'locker.nicknames': 'Nicknames',
  'locker.nicknameNone': 'No nickname',
  'locker.kits': 'Kits',
  'locker.kitCount': '{count}',
  'locker.slot.marker': 'Marker',
  'locker.slot.nametag': 'Name tag',
  'locker.slot.stamp': 'Stamps',
  'locker.slot.frame': 'Result frame',
  'locker.slot.envelope': 'Envelope face',
  'locker.odds': 'Envelope odds',
  'locker.oddsNote': 'SR or better within {rare} envelopes. SSR within {legend}',
  'locker.recent': 'Recent envelopes',
  'locker.empty': 'No envelopes opened yet',
  'locker.summary': '{pins} pins, {opened} {opened|envelope|envelopes} opened',
  'locker.collection': 'Collected {percent}% ({owned} / {total})',
  'locker.collectionOf': '{owned} / {total}',
  'locker.completionLocked': 'Collect all',
  'locker.completionNote': 'Collect everything the envelopes hold and one opens in each slot',

  /* --- lobby (roadmap M6) --------------------------------------------------- */

  'lobby.eyebrow': 'Party',
  'lobby.form.title': 'Make a room or join one',
  'lobby.name.label': 'Name',
  'lobby.name.default': 'Detective',
  'lobby.create': 'Make a room',
  'lobby.code.label': 'Room code',
  'lobby.join': 'Join by code',
  'lobby.back': 'Back',
  'lobby.connecting': 'Entering the room',
  'lobby.codeAria': 'Room code {code}',
  'lobby.share': 'Share this code and they join the same room',
  'lobby.seats': '{count}/{max}',
  'lobby.seatsAria': '{count} {count|player|players}. Up to {max}',
  'lobby.tag.host': 'Host',
  'lobby.tag.neighbor': 'Neighbor',
  'lobby.tag.ready': 'Ready',
  'lobby.tag.waiting': 'Not ready',
  'lobby.tag.away': 'Away',
  'lobby.addNeighbor': 'Add a neighbor',
  'lobby.removeNeighbor': 'Remove a neighbor',
  'lobby.ready': 'Ready',
  'lobby.unready': 'Not ready',
  'lobby.start': 'Start',
  'lobby.startHint': 'Two or more, everyone ready, then you can start',
  'lobby.startWait': 'The host starts the round',
  'lobby.settings': 'Host settings',
  'lobby.settings.verbs': 'Kinds of oddities',
  'lobby.settings.count': 'Oddities per person',
  'lobby.settings.less': 'One fewer',
  'lobby.settings.more': 'One more',
  /*
   * Decorate toggle (M10). The name matches the phase (`phase.decorate.name`). The chip's text is what
   * turning it ON means, not "on/off" - `aria-pressed` already carries the state, and flipping the
   * label too would say the same thing twice, in opposite directions (same rule as the verb chips).
   */
  'lobby.settings.decorate': 'Decorate',
  'lobby.settings.decorate.on': 'Furnish an empty room together',

  /* --- connection (roadmap M6) ---------------------------------------------- */

  'net.reconnecting': 'Connection lost. Reconnecting',
  'net.resumed': 'Back in your seat',
  'net.closed': 'Disconnected',
  'net.error.version': 'New version. Reload the page',
  'net.error.no-room': 'No room with that code',
  'net.error.full': 'The room is full',
  'net.error.in-progress': 'That room already started',
  'net.error.superseded': 'Another window took this seat',
  'net.error.offline': "Couldn't reach the room server. Try again in a moment",

  /* --- errors -------------------------------------------------------------- */

  'error.bootFailed': 'The game could not start',
  'error.bootDetail': 'If a reload does not help, note the room code and tell us',
  'error.saveFailed': 'Progress did not save. You can still go on this once',
  'error.assetsFallback': 'Some models did not load, using stand-in shapes',
}
